import React, {useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {Edges} from "@react-three/drei";
import {Box3, Color, SRGBColorSpace, TextureLoader, Vector3} from "three";
import {createHandleObject, DEFAULT_HANDLE_SIZE_MM} from "./tds/parse3dsToHandle";
import {
    DEFAULT_HANDLE_MATERIAL_ID,
    resolveHandleMaterialProps,
} from "./handleMaterials";

/**
 * Mesh ручки из 3DS.
 * materialProps.fromFile — не перекрывать материалы каталогом (вид из 3DS).
 * Смена пресета каталога — только через мутацию material (без пересоздания mesh).
 */
const HandleMesh = ({
    modelBase64,
    sizeMm = DEFAULT_HANDLE_SIZE_MM,
    materialId = DEFAULT_HANDLE_MATERIAL_ID,
    materialProps: materialPropsProp = null,
    selected = false,
}) => {
    const mapRef = useRef(null);

    const materialProps = useMemo(() => {
        if (materialPropsProp) return materialPropsProp;
        return resolveHandleMaterialProps(materialId);
    }, [materialPropsProp, materialId]);

    const fromFile = Boolean(materialProps?.fromFile);

    // Пересоздаём только геометрию / режим «из файла» — не на каждый клик по пресету
    const object = useMemo(() => {
        if (!modelBase64) return null;
        try {
            return createHandleObject(modelBase64, sizeMm);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error("HandleMesh: failed to build 3DS object", err);
            return null;
        }
    }, [modelBase64, sizeMm, fromFile]);

    const [mapVersion, setMapVersion] = useState(0);

    useEffect(() => {
        const url = fromFile ? null : materialProps?.mapUrl;
        if (mapRef.current) {
            mapRef.current.dispose();
            mapRef.current = null;
        }
        if (!url) {
            setMapVersion((v) => v + 1);
            return undefined;
        }
        let cancelled = false;
        const loader = new TextureLoader();
        loader.load(
            url,
            (tex) => {
                if (cancelled) {
                    tex.dispose();
                    return;
                }
                tex.colorSpace = SRGBColorSpace;
                tex.needsUpdate = true;
                mapRef.current = tex;
                setMapVersion((v) => v + 1);
            },
            undefined,
            () => {
                if (!cancelled) setMapVersion((v) => v + 1);
            },
        );
        return () => {
            cancelled = true;
            if (mapRef.current) {
                mapRef.current.dispose();
                mapRef.current = null;
            }
        };
    }, [materialProps?.mapUrl, fromFile]);

    useLayoutEffect(() => {
        if (!object) return undefined;
        const map = mapRef.current;
        const color = fromFile ? null : new Color(materialProps.color);

        object.traverse((child) => {
            if (!child.isMesh) return;
            const materials = Array.isArray(child.material)
                ? child.material
                : [child.material];
            materials.forEach((mat) => {
                if (!mat) return;
                if (!fromFile) {
                    if (mat.color && color) mat.color.copy(color);
                    if ("metalness" in mat) mat.metalness = materialProps.metalness;
                    if ("roughness" in mat) mat.roughness = materialProps.roughness;
                    if ("map" in mat) {
                        mat.map = map || null;
                    }
                }
                if (!mat.userData.__handleEmissiveSaved && mat.emissive) {
                    mat.userData.__handleEmissiveSaved = mat.emissive.clone();
                }
                if (mat.emissive) {
                    if (selected) {
                        mat.emissive.setHex(0x1565c0);
                        if ("emissiveIntensity" in mat) mat.emissiveIntensity = 0.35;
                    } else if (mat.userData.__handleEmissiveSaved) {
                        mat.emissive.copy(mat.userData.__handleEmissiveSaved);
                        if ("emissiveIntensity" in mat) mat.emissiveIntensity = 1;
                    } else {
                        mat.emissive.setHex(0x000000);
                        if ("emissiveIntensity" in mat) mat.emissiveIntensity = 1;
                    }
                }
                mat.needsUpdate = true;
            });
        });
        return undefined;
    }, [object, materialProps, selected, mapVersion, fromFile]);

    const edgeBox = useMemo(() => {
        if (!object || !selected) return null;
        const box = new Box3().setFromObject(object);
        if (box.isEmpty()) return null;
        const size = new Vector3();
        const center = new Vector3();
        box.getSize(size);
        box.getCenter(center);
        return {size, center};
    }, [object, selected]);

    if (!object) return null;

    return (
        <group>
            <primitive object={object}/>
            {edgeBox && (
                <mesh position={edgeBox.center.toArray()}>
                    <boxGeometry args={[edgeBox.size.x, edgeBox.size.y, edgeBox.size.z]}/>
                    <meshBasicMaterial visible={false}/>
                    <Edges color="#1565c0" threshold={15}/>
                </mesh>
            )}
        </group>
    );
};

export default HandleMesh;
